import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

function normalize(value, fallback = "") {
  return String(value || fallback).trim().toLowerCase();
}

// Règle produit ARTKIV :
// - collectionneur Standard : pas d'accès aux modules IA ;
// - collectionneur Premium : accès IA sous réserve d'un consentement explicite ;
// - les plans professionnels disposant de fonctions IA restent éligibles selon leur offre ;
// - un compte démo autorisé peut tester les fonctions IA.
function hasAiEntitlement(profile = {}) {
  const role = normalize(profile?.role, "user");
  const plan = normalize(profile?.plan || profile?.subscription_plan, "standard");

  if (profile?.is_demo === true) return true;
  if (["admin", "team"].includes(role)) return true;

  if (role === "user") {
    return ["premium", "family_office"].includes(plan);
  }

  if (role === "expert" || role === "art_advisor") {
    return ["art_advisor", "art_advisor_premium", "premium"].includes(plan);
  }

  if (role === "family_office") return true;

  return ["premium", "art_advisor", "art_advisor_premium", "family_office"].includes(plan);
}

const ConsentementIA = ({ onConsentChange, embedded = false, profile: profileProp = null }) => {
  const auth = useAuth() || {};
  const user = auth.user;
  const authProfile = auth.profile || null;
  const { t, i18n } = useTranslation();

  const [consent, setConsent] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDetails, setShowDetails] = useState(false);

  const eligibleForAI = useMemo(
    () => hasAiEntitlement(profileProp || authProfile || {}),
    [profileProp, authProfile]
  );

  const onConsentChangeRef = useRef(onConsentChange);
  useEffect(() => {
    onConsentChangeRef.current = onConsentChange;
  }, [onConsentChange]);

  useEffect(() => {
    let cancelled = false;

    const loadConsent = async () => {
      if (!user?.id) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage("");

      const { data, error } = await supabase
        .from("profiles")
        .select("uses_ai, ai_signed_at")
        .eq("id", user.id)
        .single();

      if (cancelled) return;

      if (error) {
        console.error("[ConsentementIA] Erreur lecture profil :", error);
        setErrorMessage(t("consentIA.error.load"));
        setLoading(false);
        return;
      }

      // Gate active = abonnement éligible + uses_ai=true + consentement horodaté.
      // On conserve ai_signed_at même après un retrait pour ne pas effacer
      // l'existence du dernier accord horodaté. uses_ai reste la source du statut actif.
      const recordedSignedAt = data?.ai_signed_at || null;
      const value = Boolean(data?.uses_ai === true && recordedSignedAt);

      setConsent(value);
      setSignedAt(recordedSignedAt);
      setLoading(false);
      onConsentChangeRef.current?.(value, {
        source: "load",
        signedAt: recordedSignedAt,
        eligibleForAI,
      });
    };

    loadConsent();
    return () => {
      cancelled = true;
    };
  }, [eligibleForAI, t, user?.id]);

  const toggleConsent = async (event) => {
    if (!user?.id || saving) return;

    const nextValue = event.target.checked;

    // Un compte non éligible peut toujours RETIRER un ancien consentement,
    // mais ne peut pas en activer un nouveau tant que l'offre IA n'est pas disponible.
    if (nextValue && !eligibleForAI) {
      setErrorMessage(t("consentIA.access.upgradeRequired"));
      return;
    }

    const previousValue = consent;
    const previousSignedAt = signedAt;
    const now = new Date().toISOString();
    const nextSignedAt = nextValue ? now : previousSignedAt;

    setErrorMessage("");
    setSuccessMessage("");
    setSaving(true);
    setConsent(nextValue);
    setSignedAt(nextSignedAt);

    const updatePayload = nextValue
      ? {
          uses_ai: true,
          ai_signed_at: now,
          updated_at: now,
        }
      : {
          // Ne pas effacer ai_signed_at : il reste la trace du dernier accord.
          // Le gate applicatif exige toujours uses_ai === true ET ai_signed_at présent.
          uses_ai: false,
          updated_at: now,
        };

    const { error } = await supabase
      .from("profiles")
      .update(updatePayload)
      .eq("id", user.id);

    if (error) {
      console.error("[ConsentementIA] Erreur MAJ :", error);
      setConsent(previousValue);
      setSignedAt(previousSignedAt);
      setErrorMessage(t("consentIA.error.save"));
      onConsentChangeRef.current?.(previousValue, {
        source: "rollback",
        signedAt: previousSignedAt,
        eligibleForAI,
      });
      setSaving(false);
      return;
    }

    setSuccessMessage(
      nextValue
        ? t("consentIA.success.enabled")
        : t("consentIA.success.disabled")
    );

    onConsentChangeRef.current?.(nextValue, {
      source: "save",
      signedAt: nextSignedAt,
      eligibleForAI,
    });
    setSaving(false);
  };

  const formatSignedAt = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleString(
        i18n.resolvedLanguage || i18n.language || "fr"
      );
    } catch {
      return value;
    }
  };

  const wrapperClass = embedded
    ? "p-6 bg-white border border-gray-200 rounded-lg"
    : "p-6 bg-white shadow rounded-lg mt-12";

  if (loading) {
    return (
      <div className={wrapperClass}>
        <div className="animate-pulse text-gray-500">
          {t("consentIA.loading")}
        </div>
      </div>
    );
  }

  const purposeCards = [
    ["visual", "01"],
    ["documents", "02"],
    ["conservation", "03"],
    ["valuation", "04"],
  ];

  const statusKey = !eligibleForAI
    ? "standard"
    : consent
    ? "enabled"
    : "waitingConsent";

  return (
    <div className={wrapperClass}>
      {!embedded && (
        <h2 className="text-xl font-semibold mb-2">{t("consentIA.title")}</h2>
      )}

      <p className="text-sm text-gray-700 mb-4 leading-relaxed">
        {t("consentIA.description")}
      </p>

      <div
        className={[
          "mb-5 rounded-lg border p-4",
          statusKey === "enabled"
            ? "border-green-200 bg-green-50"
            : statusKey === "waitingConsent"
            ? "border-amber-200 bg-amber-50"
            : "border-gray-200 bg-gray-50",
        ].join(" ")}
      >
        <p className="text-sm font-semibold text-gray-900 mb-1">
          {t(`consentIA.access.${statusKey}.title`)}
        </p>
        <p className="text-xs text-gray-600 leading-relaxed">
          {t(`consentIA.access.${statusKey}.body`)}
        </p>
        {!eligibleForAI && !consent && (
          <Link
            to="/upgrade-downgrade"
            className="inline-flex mt-3 text-xs font-semibold underline"
            style={{ color: "#8A6D1F" }}
          >
            {t("consentIA.access.viewPlans")}
          </Link>
        )}
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"
        >
          {successMessage}
        </div>
      )}

      <label
        className={[
          "flex items-start gap-3 rounded-lg border-2 p-4 transition-colors",
          !eligibleForAI && !consent
            ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-70"
            : "cursor-pointer border-gray-200 hover:border-[#C9A961]",
        ].join(" ")}
      >
        <input
          type="checkbox"
          checked={consent}
          onChange={toggleConsent}
          disabled={saving || !user?.id || (!eligibleForAI && !consent)}
          aria-describedby="ai-consent-help"
          style={{
            accentColor: "#c9a961",
            width: 18,
            height: 18,
            marginTop: 2,
          }}
        />
        <span className="text-sm font-medium text-gray-900 leading-relaxed">
          {t("consentIA.checkboxLabel")}
        </span>
      </label>

      <div id="ai-consent-help" className="mt-3 space-y-1">
        {saving && (
          <p className="text-xs text-gray-500">{t("consentIA.saving")}</p>
        )}

        {consent && signedAt && !saving && (
          <p className="text-xs text-gray-500">
            {t("consentIA.signedAt", { date: formatSignedAt(signedAt) })}
          </p>
        )}

        {!consent && signedAt && !saving && (
          <p className="text-xs text-gray-500">
            {t("consentIA.previousConsent", { date: formatSignedAt(signedAt) })}
          </p>
        )}

        <p className="text-xs text-gray-500">{t("consentIA.controlHint")}</p>
        <p className="text-xs text-gray-500">
          {t("consentIA.moreInfo.text")} {" "}
          <Link
            to="/consentement-ia"
            className="underline"
            style={{ color: "#c9a961" }}
          >
            {t("consentIA.moreInfo.link")}
          </Link>
          .
        </p>
      </div>

      {/* Détail des traitements — repliable : le texte légal/éducatif reste
          disponible en entier, mais n'écrase plus l'action au chargement. */}
      <div className="mt-5 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          className="flex w-full items-center justify-between text-sm font-semibold text-gray-900"
        >
          {t("consentIA.detailsToggle", { defaultValue: "Voir le détail des traitements" })}
          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform ${showDetails ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        {showDetails && (
          <div className="mt-4 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t("consentIA.purposes.title")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {purposeCards.map(([key, number]) => (
                  <div
                    key={key}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xs font-semibold text-[#8A6D1F] mt-0.5">
                        {number}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          {t(`consentIA.purposes.${key}.title`)}
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {t(`consentIA.purposes.${key}.body`)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-[#E7D9B4] bg-[#FBF7EC] p-4">
              <h3 className="text-sm font-semibold text-[#6B531A] mb-2">
                {t("consentIA.data.title")}
              </h3>
              <p className="text-xs text-[#6B531A] leading-relaxed">
                {t("consentIA.data.body")}
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t("consentIA.guardrails.title")}
              </h3>
              <ul className="space-y-2 text-xs text-gray-600 leading-relaxed">
                <li className="flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{t("consentIA.guardrails.suggestions")}</span>
                </li>
                <li className="flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{t("consentIA.guardrails.noAuthentication")}</span>
                </li>
                <li className="flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{t("consentIA.guardrails.humanValidation")}</span>
                </li>
                <li className="flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{t("consentIA.guardrails.professional")}</span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                {t("consentIA.scope.title")}
              </h3>
              <p className="text-xs text-blue-800 leading-relaxed mb-2">
                {t("consentIA.scope.account")}
              </p>
              <p className="text-xs text-blue-800 leading-relaxed">
                {t("consentIA.scope.artworkOptIn")}
              </p>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">
              {t("consentIA.withoutAI")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentementIA;
